import { ServiceContainerDto } from "@apiModels/ServiceContainerDto.csharp";
import { TreatmentsContainerDto } from "@apiModels/TreatmentsContainerDto.csharp";
import { TreatmentContainerType } from "@apiModels/TreatmentContainerType.csharp";
import { LocationDto } from "@apiModels/LocationDto.csharp";
import { DoctorDto } from "@apiModels/DoctorDto.csharp";
import {
  FormDispatch,
  FormFields,
  FormReducer,
  FormStateType,
} from "./formTypes";
import { parentTreatmentInitialState } from "./initialState/parentTreatmentInitialState";
import { StepName } from "../Hooks/form";
import { setStorage } from "./formFunctions";

export function findTreatmentById(
  treatments: (TreatmentsContainerDto | ServiceContainerDto)[],
  id: string
): TreatmentsContainerDto | ServiceContainerDto | null {
  for (const treatment of treatments) {
    if (treatment.containerId === id) {
      return treatment;
    }

    if (isTreatmentContainer(treatment)) {
      const found = findTreatmentById(treatment.items, id);
      if (found) return found;
    }
  }
  return null;
}

export function getTreatmentDepth(
  treatments: (TreatmentsContainerDto | ServiceContainerDto)[],
  treatment: TreatmentsContainerDto | ServiceContainerDto | null
): number {
  let depth = 0;
  while (treatment && treatment.parentContainerId) {
    treatment = findTreatmentById(treatments, treatment.parentContainerId);
    if (treatment) depth++;
  }
  return depth;
}

export function isTreatmentContainer(
  treatment: TreatmentsContainerDto | ServiceContainerDto
): treatment is TreatmentsContainerDto {
  return treatment.type === TreatmentContainerType.Treatments;
}

export function isServiceContainer(
  treatment: TreatmentsContainerDto | ServiceContainerDto
): treatment is ServiceContainerDto {
  return treatment.type === TreatmentContainerType.Service;
}

export function filterTreatment(
  treatment: TreatmentsContainerDto | ServiceContainerDto,
  location: LocationDto,
  doctor: DoctorDto,
  isDoctorPageBooking?: boolean
): (TreatmentsContainerDto | ServiceContainerDto)[] {
  const isDoctorFiltered = doctor.id && doctor.id !== "-1";
  if (
    isDoctorPageBooking &&
    isDoctorFiltered &&
    treatment.hideInDoctorBooking
  ) {
    return [];
  }
  if (isServiceContainer(treatment)) {
    let passesFilters = true;

    if (location.id && location.id !== "-1") {
      passesFilters = treatment.centerBookingOptions.some(
        (bookingOption) =>
          bookingOption.centerId === location.id ||
          (location.centers &&
            location.centers.includes(bookingOption.centerId))
      );
    }
    if (passesFilters && isDoctorFiltered) {
      passesFilters = doctor.services.includes(treatment.id);
    }

    return passesFilters ? [treatment] : [];
  }

  if (isTreatmentContainer(treatment)) {
    const filteredChildren = treatment.items
      .map((treatment) =>
        filterTreatment(treatment, location, doctor, isDoctorPageBooking)
      )
      .flat();

    if (filteredChildren.length === 0) return [];

    if (
      isDoctorPageBooking &&
      isDoctorFiltered &&
      treatment.ungroupInDoctorBooking
    ) {
      return filteredChildren;
    }

    return [
      {
        ...treatment,
        items: filteredChildren,
      },
    ];
  }

  return [];
}

export function filterTreatmentItems(
  treatments: (TreatmentsContainerDto | ServiceContainerDto)[],
  location: LocationDto,
  doctor: DoctorDto,
  defaultTreatmentsContainer?: TreatmentsContainerDto,
  isDoctorPageBooking?: boolean
): (TreatmentsContainerDto | ServiceContainerDto)[] {
  if (defaultTreatmentsContainer?.containerId) {
    return filterTreatment(
      defaultTreatmentsContainer,
      location,
      doctor,
      isDoctorPageBooking
    );
  }

  return treatments
    .map((treatment) =>
      filterTreatment(treatment, location, doctor, isDoctorPageBooking)
    )
    .flat();
}

export function getServiceContainersFromTreatments(
  items: (TreatmentsContainerDto | ServiceContainerDto)[]
): ServiceContainerDto[] {
  let results: ServiceContainerDto[] = [];

  for (const item of items) {
    if (isServiceContainer(item)) {
      results.push(item);
    } else if (isTreatmentContainer(item)) {
      results = results.concat(getServiceContainersFromTreatments(item.items));
    }
  }

  return results;
}

export function findClosestParentContainerWithSpecializationName(
  serviceContainer: ServiceContainerDto,
  treatments: (TreatmentsContainerDto | ServiceContainerDto)[]
): string {
  if (serviceContainer.isSpecialization) return serviceContainer.bookingName;

  let parentId = serviceContainer.parentContainerId;

  while (parentId) {
    const parent = findTreatmentById(treatments, parentId);
    if (!parent) break;

    if (isTreatmentContainer(parent) && parent.isSpecialization) {
      return parent.summaryName ?? parent.bookingName;
    }

    parentId = parent.parentContainerId;
  }

  return serviceContainer.parentContainerId
    ? findTreatmentById(treatments, serviceContainer.parentContainerId)
        ?.bookingName ?? ""
    : "";
}

export function selectContainer(
  container: TreatmentsContainerDto | ServiceContainerDto | null,
  treatments: (TreatmentsContainerDto | ServiceContainerDto)[],
  dispatch: FormDispatch,
  state: FormStateType,
  setStepByName: (step: StepName) => void,
  breadcrumbs?: string[]
) {
  if (!container) return;

  const parentContainer = container.parentContainerId
    ? findTreatmentById(treatments, container.parentContainerId)
    : null;

  if (isServiceContainer(container)) {
    const specializationName = findClosestParentContainerWithSpecializationName(
      container,
      treatments
    );

    dispatch({
      type: FormReducer.update,
      field: FormFields.service,
      payload: container,
    });

    dispatch({
      type: FormReducer.update,
      field: FormFields.treatment,
      payload: container,
    });

    dispatch({
      type: FormReducer.update,
      field: FormFields.parentTreatment,
      payload: parentContainer || parentTreatmentInitialState,
    });

    dispatch({
      type: FormReducer.update,
      field: FormFields.specializationName,
      payload: specializationName,
    });

    if (breadcrumbs) {
      dispatch({
        type: FormReducer.update,
        field: FormFields.breadcrumbs,
        payload: breadcrumbs,
      });
    }

    setStepByName("AppointmentStep");
  } else if (isTreatmentContainer(container)) {
    dispatch({
      type: FormReducer.update,
      field: FormFields.treatment,
      payload: container,
    });

    setStepByName("TreatmentStep");
  }

  setStorage(state);
}
